import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import {
  ServiceRequestService,
  ServiceRequestVM,
  MarkAsFixedServiceRequestVM,
  NotesEntityTypesEnum,
  DocumentService,
  DocumentEntityTypesEnum,
  DocumentTypesEnum,
  DocumentVM,
  PayPaymentDueAccountVM,
  PaymentMethodTypesEnum,
  PaymentService,
  AccessLevelEnum,
} from "@propertelligent/client-api";
import apiHelper from "../../utils/apiHelper";
import Stepper from "./components/stepper";
import EstimateForm from "./components/estimate-form";
import EditServiceRequestForm from "../../newComponents/serviceRequest/EditServiceRequestForm";
import ServiceRequestCardItem from "../../newComponents/serviceRequest/ServiceRequestCardItem";
import AddNotesForm from "../../newComponents/notes/AddNotesForm";
import { ConfirmationPopup } from "../../newComponents/confirmation-popup/confirmation-popup-demo";
import RightSideModal from "../../newComponents/Modal/right-side-modal";
import SelectField from "../../newComponents/form-fields/SelectField";
import InputField from "../../newComponents/form-fields/InputField";
import { UserLoginData } from "../../components/common/charts/Enums";
import "./ServiceRequestDetails.css";
import Card from "../../newComponents/card";
import SimpleSpinner from "../../components/loader";
import { useCurrencySymbol } from "../../hooks/useCurrencySymbol";
import { usePermissions } from "../../hooks/usePermissions";
import NoAccess from "../NoAccess";
import { truncateDecimal } from "../../utils/truncateDecimal";
import { getEnumOptions } from "../../utils/enumHelpers";
import { isTenantOrContractorRole } from "../../utils/roleGuards";

interface ServiceRequestDetailsProps {
  serviceRequestId?: number;
  userRole?: "Landlord" | "Tenant" | "Contractor";
}

interface EditFormData {
  laborCost: string;
  materialCost: string;
  fixDate: string;
  fixNotes: string;
}

type ServiceRequestStatus =
  | "Open"
  | "Estimating"
  | "In Progress"
  | "Verification Pending"
  | "Payment Pending"
  | "Completed"
  | null
  | "";

type FormValues = {
  paymentMethodTypeId: string;
  mode: string;
  date: string;
  checkNo?: string;
  transactionId?: string;
};

export type PaymentMethod = {
  Id: string;
  Name: string;
};

// Payment recording modes -- mirrors PaymentMethodTypesEnum on the backend.
// Payables (paying a contractor) can move money via Plaid, unlike receivables.
const PAYMENT_MODE = PaymentMethodTypesEnum;
const PAYMENT_MODE_OPTIONS = getEnumOptions(PAYMENT_MODE, [
  PAYMENT_MODE.Plaid,
  PAYMENT_MODE.Cash,
  PAYMENT_MODE.Check,
  PAYMENT_MODE.Other,
]);

export default function ServiceRequestDetails({
  serviceRequestId = Number(useParams().id),
  userRole = "Landlord",
}: ServiceRequestDetailsProps) {
  const [showAssignmentView, setShowAssignmentView] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [showInProgressView, setShowInProgressView] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [isInHouse, setIsInHouse] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditLoading, setEditIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [serviceRequestStatus, setServiceRequestStatus] = useState<ServiceRequestStatus>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isNoteModalOpen, setNoteModalOpen] = useState(false);
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [serviceRequestDetails, setServiceRequestDetails] = useState<ServiceRequestVM | null>(null);
  const [workPhotos, setWorkPhotos] = useState<string[]>([]);
  const navigate = useNavigate();
  const currencySymbol = useCurrencySymbol();
  const { hasAccess, canWrite } = usePermissions();

  const methods = useForm<EditFormData>({
    defaultValues: {
      laborCost: "",
      materialCost: "",
      fixDate: "",
      fixNotes: "",
    },
  });

  const paymentMethodsForm = useForm<FormValues>({
    mode: "onChange",
    defaultValues: {
      paymentMethodTypeId: "",
      mode: "",
      date: new Date().toISOString().split("T")[0],
      checkNo: "",
      transactionId: "",
    },
  });

  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { control, resetField, trigger: triggerPaymentValidation, formState: { isDirty: isPaymentFormDirty, isValid: isPaymentFormValid } } = paymentMethodsForm;
  const selectedPaymentModeType = Number(useWatch({ control, name: "paymentMethodTypeId" }));
  const orgId = parseInt(sessionStorage.getItem(UserLoginData.organization_id) || '0');

  // Fetch payment methods when payment modal opens
  useEffect(() => {
    const fetchPaymentMethods = async () => {
      try {
        const response = await apiHelper(
          PaymentService.getApiPaymentMethodOrganizationlist,
          { showNotification: false },
          orgId
        );
        if (response.data?.success) {
          const methods: PaymentMethod[] = response.data.success
            .filter((item: any) => item.paymentMethodTypeId === PAYMENT_MODE.Plaid)
            .map((item: any) => ({
              Id: item.id.toString(),
              Name: item.accountName,
            }));
          setPaymentMethods(methods);
        }
      } catch (error) {
        setError("Failed to fetch payment methods");
        console.error(error);
      }
    };

    if (isPaymentModalOpen) {
      fetchPaymentMethods();
    }
  }, [isPaymentModalOpen, orgId]);

  // Reset conditional fields when the payment mode changes. resetField clears any stale
  // "required" error left over from a previously-selected mode (e.g. Check's checkNo), but
  // formState.isValid is not always recomputed synchronously after resetField on the very first
  // mode change -- explicitly re-trigger validation so Save/Pay enables immediately for a mode
  // (like Cash) that has no unmet requirements of its own, even when it is the first mode ever
  // selected in this form instance.
  useEffect(() => {
    resetField("mode");
    resetField("checkNo");
    resetField("transactionId");
    triggerPaymentValidation();
  }, [selectedPaymentModeType, resetField, triggerPaymentValidation]);

  // Fetch service request details

  useEffect(() => {


    fetchServiceRequestDetails();
  }, [serviceRequestId, methods]);


  const fetchServiceRequestDetails = async () => {

    isEditLoading ? setIsLoading(false) : setIsLoading(true);

    setError(null);
    try {
      const response = await apiHelper(
        ServiceRequestService.getApiServiceRequest1,
        { showNotification: false },
        serviceRequestId
      );
      if (response?.data?.success) {
        const data: ServiceRequestVM = response.data.success;
        const status: ServiceRequestStatus = data.formatted?.ServiceRequestStatus || null;
        setServiceRequestDetails(data);
        setServiceRequestStatus(status);
        setIsInHouse(data.isInHouse ?? true);

        methods.reset({
          laborCost: data.laborCost?.toString() || "",
          materialCost: data.materialCost?.toString() || "",
          fixDate: data.completeDate ? new Date(data.completeDate).toISOString() : "",
          fixNotes: data.fixNotes || "",
        });

        // Fetch work photos
        try {
          const listResponse = await apiHelper(
            DocumentService.getApiDocumentList,
            { showNotification: false },
            serviceRequestId,
            DocumentEntityTypesEnum.ServiceRequest,
            DocumentTypesEnum.AfterImage
          );

          if (listResponse?.data?.success && listResponse.data.success.length > 0) {
            const documentArray: DocumentVM[] = listResponse.data.success;
            const photoPromises = documentArray.map(doc =>
              apiHelper(
                DocumentService.getApiDocumentBase641,
                { showNotification: false },
                doc.id
              )
            );
            const base64Responses = await Promise.all(photoPromises);
            const photoUrls = base64Responses
              .filter(res => res?.data?.success)
              .map(res => `data:image/png;base64,${res.data.success}`);
            setWorkPhotos(photoUrls);
          }
        } catch (photoError) {
          console.error("Failed to fetch work photos", photoError);
        }

        // Set stepper and view states based on status
        switch (status) {
          case "Open":
          case "Estimating":
            setCurrentStep(0);
            setShowAssignmentView(false);
            setShowInProgressView(false);
            setIsVerified(false);
            break;
          case "In Progress":
            setCurrentStep(1);
            setShowAssignmentView(true);
            setShowInProgressView(false);
            setIsVerified(false);
            break;
          case "Verification Pending":
            setCurrentStep(2);
            setShowAssignmentView(false);
            setShowInProgressView(true);
            setIsVerified(false);
            break;
          case "Payment Pending":
            setCurrentStep(3);
            setShowAssignmentView(false);
            setShowInProgressView(true);
            setIsVerified(false);
            break;
          case "Completed":
            setCurrentStep(isInHouse ? 2 : 3);
            setShowAssignmentView(false);
            setShowInProgressView(true);
            setIsVerified(true);
            break;
          default:
            setCurrentStep(0);
            setShowAssignmentView(false);
            setShowInProgressView(false);
            setIsVerified(false);
        }
      }
    } catch (err) {
      setError("Failed to load service request details");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle mark as fixed
  const handleMarkAsFixed = async (data: EditFormData) => {
    setIsLoading(true);
    setError(null);
    try {
      if (!data.fixDate) {
        setError("Fix date is required");
        setIsLoading(false);
        return;
      }

      const requestBody: MarkAsFixedServiceRequestVM = {
        fixDate: new Date(data.fixDate).toISOString(),
        laborCost: data.laborCost ? parseFloat(data.laborCost) : null,
        materialCost: data.materialCost ? parseFloat(data.materialCost) : null,
        fixNotes: data.fixNotes || null,
      };

      const response = await apiHelper(
        ServiceRequestService.putApiServiceRequestMarkAsFixed,
        {
          successMessage: "Service request updated successfully",
          showNotification: true,
        },
        serviceRequestId,
        requestBody
      );

      if (response?.data?.success) {
        setShowInProgressView(true);
        await fetchServiceRequestDetails();
      }
    } catch (err) {
      setError("Failed to update fix details");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle edit service request
  const handleEdit = async () => {

    setError(null);
    try {
      setEditIsLoading(true);
      setEditModalOpen(true);
      await fetchServiceRequestDetails();

    } catch (err) {
      setError("Failed to load service request for editing");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle reassign
  const handleReassign = () => {
    setCurrentStep(0);
    setShowInProgressView(false);
    setShowAssignmentView(false);
    setServiceRequestStatus("Estimating");
  };

  // Handle edit estimate
  const handleEditEstimate = () => {
    setShowInProgressView(false);
    setShowAssignmentView(true);
  };



  // Handle add note
  const handleAddNote = (id: number) => {
    setNoteModalOpen(true);
  };

  // Handle action (verify or pay)
  const handleAction = () => {
    setShowConfirmation(true);
  };

  // Handle confirmation for verify or pay
  const handleConfirmAction = async () => {
    setIsLoading(true);
    setError(null);
    try {
      let response;
      if (serviceRequestStatus === "Payment Pending") {
        setShowConfirmation(false);
        setIsPaymentModalOpen(true);
      } else {
        response = await apiHelper(
          ServiceRequestService.putApiServiceRequestVerify,
          {
            successMessage: "Service request verified successfully",
            showNotification: true,
          },
          serviceRequestId
        );

        if (response?.data?.success) {
          setCurrentStep(isInHouse ? 2 : 3);
          setIsVerified(true);
          setShowConfirmation(false);
          setServiceRequestStatus("Completed");
          await fetchServiceRequestDetails();
        } else if (response?.data?.errors) {
          const errorMessage =
            Object.values(response.data.errors).flat().join(", ") || "Action failed";
          setError(errorMessage);
          setShowConfirmation(false);
        }
      }
    } catch (err) {
      setError(
        `Failed to ${serviceRequestStatus === "Payment Pending" ? "pay" : "verify"} service request`
      );
      console.error(err);
      setShowConfirmation(false);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle payment submission
  const handlePostPayment = async (formData: FormValues) => {
    setIsSubmitting(true);
    setError(null);

    const paymentMethodTypeId = Number(formData.paymentMethodTypeId);
    const totalCost = (serviceRequestDetails?.laborCost || 0) + (serviceRequestDetails?.materialCost || 0);

    const payload: PayPaymentDueAccountVM = {
      payDueAccountId: serviceRequestDetails?.paymentDueAccountId ?? serviceRequestId,
      amount: totalCost,
      paymentMethodTypeId,
      paymentDate: new Date(formData.date).toISOString(),
    };

    if (paymentMethodTypeId === PAYMENT_MODE.Plaid) {
      if (!formData.mode) {
        setError("Payment method not selected or found.");
        setIsSubmitting(false);
        return;
      }
      payload.fromPaymentMethodId = parseInt(formData.mode);
    } else if (paymentMethodTypeId === PAYMENT_MODE.Check) {
      payload.checkNumber = formData.checkNo || "";
    } else if (paymentMethodTypeId === PAYMENT_MODE.Other) {
      payload.transactionId = formData.transactionId || "";
    }

    try {
      const response = await apiHelper(
        PaymentService.putApiPaymentDueAccountPay,
        {
          // Render the actual API response message (e.g. the pending-confirmation wording for a
          // Plaid transfer that hasn't cleared yet) instead of an unconditional hardcoded string --
          // see bug/plaid-optimistic-paid.
          successMessage: (res: { success?: string }) =>
            res?.success || "Payment posted successfully!",
          failureMessage: "Failed to post payment.",
          showNotification: true,
        },
        payload
      );

      if (response?.data?.success) {
        setIsPaymentModalOpen(false);
        paymentMethodsForm.reset();
        setCurrentStep(3);
        setServiceRequestStatus("Completed");
        navigate(-1);
        await fetchServiceRequestDetails();



      }
    } catch (error) {
      setError("Payment failed to post");
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle cancel confirmation
  const handleCancelConfirmation = () => {
    setShowConfirmation(false);
  };

  // Handle edit modal close
  const handleEditModalClose = async () => {
    setEditModalOpen(false);
    await fetchServiceRequestDetails();
  };

  const hasPayStep = !isInHouse && serviceRequestStatus === "Payment Pending";
  const actionButtonLabel = hasPayStep ? "Pay" : "Verify";
  const isCheck = selectedPaymentModeType === PAYMENT_MODE.Check;
  const showTransactionId = selectedPaymentModeType === PAYMENT_MODE.Other;
  // Mirrors PayablesPostPayment.tsx's submitButtonLabel precedent: the payment-posting button
  // always says "Pay" when Plaid is selected (a real money transfer), not the generic
  // "Post Payment" label used for the manual recording modes (Cash/Check/Other).
  const isPlaidSelected = selectedPaymentModeType === PAYMENT_MODE.Plaid;
  const totalCost = truncateDecimal((serviceRequestDetails?.laborCost || 0) + (serviceRequestDetails?.materialCost || 0));



  // See src/utils/roleGuards.ts: Tenant and Contractor sessions bypass the ServiceRequest
  // module-permission gate here (they're authorized by backend ownership instead).
  const isTenantOrContractor = isTenantOrContractorRole(userRole);
  // Edit is Tenant-only -- Contractors may Add Notes but never edit/delete a Service Request.
  const isTenant = userRole === "Tenant";

  if (!isTenantOrContractor && !hasAccess("ServiceRequest", AccessLevelEnum.ReadOnly)) {
    return <NoAccess />;
  }

  return (
    <div className="content-wrapper">
      {isLoading ? (
        <div className="loading-spinner d-flex justify-content-center align-items-center w-100" style={{ minHeight: "75vh" }}>
          <SimpleSpinner myClassName={"Icon50"} />
        </div>
      ) : (
        serviceRequestDetails && (
          <>
            <ServiceRequestCardItem
              className=""
              listingData={serviceRequestDetails}
              IsFooter={true}
              onEdit={(canWrite("ServiceRequest") || isTenant) ? handleEdit : undefined}
              onAddNote={(canWrite("ServiceRequest") || isTenantOrContractor) ? handleAddNote : undefined}
              fetchData={fetchServiceRequestDetails}
              isDetailPage={true}
              disableNavigation={true}
              userRole={userRole}
            />
            <FormProvider {...methods}>
              <div className="sr-main-container mt-4 mx-4 service-request-details">
                {error && <div className="error-message">{error}</div>}

                <Stepper
                  currentStep={currentStep}
                  isInHouse={isInHouse}
                  serviceRequestStatus={serviceRequestStatus}
                />
                <div className="content-wrapper">
                  <EstimateForm
                    inHouse={isInHouse}
                    showAssignmentView={showAssignmentView}
                    setShowAssignmentView={setShowAssignmentView}
                    serviceRequestId={serviceRequestId}
                    editMode={serviceRequestStatus === "In Progress"}
                    editData={methods.getValues()}
                    onEditSubmit={handleMarkAsFixed}
                    onMarkAsFixedSuccess={fetchServiceRequestDetails}
                    onReassign={handleReassign}
                    serviceRequestStatus={serviceRequestStatus}
                    showInProgressView={showInProgressView}
                    isVerified={isVerified}
                    workPhotos={workPhotos}
                    isLoading={isLoading}
                    onEditEstimateClick={handleEditEstimate}
                    actionButtonLabel={actionButtonLabel}
                    onVerifyAction={handleAction}
                    assignedToName={serviceRequestDetails?.assignedTo}
                    formattedData={serviceRequestDetails?.formatted}
                  />
                </div>

                {isNoteModalOpen && (
                  <AddNotesForm
                    entityId={serviceRequestId}
                    entityTypeId={NotesEntityTypesEnum.ServiceRequest}
                    isModalOpen={isNoteModalOpen}
                    setModalOpen={setNoteModalOpen}
                  />
                )}

                {isEditModalOpen && (
                  <EditServiceRequestForm
                    isModalOpen={isEditModalOpen}
                    setModalOpen={setEditModalOpen}
                    fetchData={fetchServiceRequestDetails}
                    response={{ success: serviceRequestDetails }}
                  />
                )}


                {showConfirmation && (
                  <ConfirmationPopup
                    isOpen={showConfirmation}
                    title={serviceRequestStatus === "Payment Pending" ? "Confirm Payment" : "Confirm Verification"}
                    message={serviceRequestStatus === "Payment Pending" ? "Are you sure you want to proceed with payment?" : "Are you sure you want to verify this service request?"}
                    primaryButtonLabel="Cancel"
                    secondaryButtonLabel="Confirm"
                    onPrimaryAction={handleCancelConfirmation}
                    onSecondaryAction={handleConfirmAction}
                    onClose={handleCancelConfirmation}
                    isPrimaryAction={false}
                  />
                )}
                {isPaymentModalOpen && (
                  <RightSideModal
                    isOpen={isPaymentModalOpen}
                    onClose={() => setIsPaymentModalOpen(false)}
                    title="Payment Details"
                    buttonlabel={isSubmitting ? "Posting..." : isPlaidSelected ? "Pay" : "Post Payment"}
                    formId="payment-form"
                    maxWidth={550}
                    isDirty={isPaymentFormDirty}
                    isSubmitDisabled={isSubmitting || !isPaymentFormDirty || !isPaymentFormValid}
                  >
                    <Card className="no-bg">
                      <FormProvider {...paymentMethodsForm}>
                        <form id="payment-form" onSubmit={paymentMethodsForm.handleSubmit(handlePostPayment)}>
                          <div className="summary-section">
                            <div className="paid-banner" aria-label="Amount to Pay">
                              <span className="paid-label">Amount to Pay</span>
                              <span className="paid-dots" aria-hidden="true" />
                              <span className="paid-amount">{currencySymbol}{totalCost}</span>
                            </div>

                            <h3 className="section-title">Choose Payment Method</h3>

                            <SelectField
                              name="paymentMethodTypeId"
                              label="Payment Mode"
                              required={true}
                              requiredMessage="Payment Mode is required"
                              options={PAYMENT_MODE_OPTIONS}
                              placeholderText="Select Payment Mode"
                              disabled={isSubmitting}
                            />

                            {selectedPaymentModeType === PAYMENT_MODE.Plaid && (
                              <SelectField
                                name="mode"
                                label="Payment Method"
                                required={true}
                                requiredMessage="Please select a payment method"
                                options={paymentMethods}
                                placeholderText="Select a payment method"
                                disabled={isSubmitting}
                              />
                            )}

                            <div className="field">
                              <InputField
                                name="date"
                                label="Payment Date"
                                type="date"
                                required={true}
                                requiredMessage="Please enter a payment date"
                                placeholder="MM/DD/YYYY"
                                disabled={isSubmitting}
                              />
                            </div>

                            {isCheck && (
                              <InputField
                                name="checkNo"
                                label="Enter Check No"
                                type="text"
                                required={true}
                                requiredMessage="Please enter a check number"
                                placeholder="Enter check number"
                                onlyNumeric={true}
                                disabled={isSubmitting}
                              />
                            )}

                            {showTransactionId && (
                              <InputField
                                name="transactionId"
                                label="Transaction ID"
                                type="text"
                                required={true}
                                requiredMessage="Please enter a transaction ID"
                                placeholder="Enter transaction ID"
                                disabled={isSubmitting}
                              />
                            )}
                          </div>
                        </form>
                      </FormProvider>
                    </Card>
                  </RightSideModal>
                )}

              </div>
            </FormProvider>
          </>
        )
      )}
    </div>
  );


}